import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type { NucleusMutationMeta } from '@nucleus-commerce/admin-kit/query';
import type {
  Attribute,
  AttributeValue,
  CreateAttributeRequest,
  CreateAttributeValueRequest,
  UpdateAttributeRequest,
  UpdateAttributeValueRequest,
} from '@nucleus-commerce/api-client/admin';

import { byPosition } from '../api/attribute-mappers';
import { attributesApi } from '../api/attributes-api';
import { dictionaryApi } from '../api/dictionary-api';
import { skuRulesApi } from '../api/sku-rules-api';

import { dictionaryKeys } from './dictionary-keys';

/*
 * The attribute list under `dictionaryKeys.attributes()` is the one copy every screen reads: the
 * product editor's option picker, the catalog setup list and the attribute editor. Each mutation
 * writes the server's answer into it at once, then the list is refetched to settle anything the
 * answer does not carry (another value's position, a concurrent edit).
 */

function editList(queryClient: QueryClient, edit: (rows: Attribute[]) => Attribute[]) {
  queryClient.setQueryData<Attribute[]>(dictionaryKeys.attributes(), (rows) =>
    rows ? edit(rows) : rows,
  );
}

function editValues(
  queryClient: QueryClient,
  attributeId: string,
  edit: (values: AttributeValue[]) => AttributeValue[],
) {
  editList(queryClient, (rows) =>
    rows.map((row) =>
      row.id === attributeId ? { ...row, values: edit(row.values).sort(byPosition) } : row,
    ),
  );
}

const mergeValues = (values: AttributeValue[], changed: readonly AttributeValue[]) => {
  const byId = new Map(changed.map((v) => [v.id, v]));
  return values.map((v) => byId.get(v.id) ?? v);
};

const refetchAttributes = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: dictionaryKeys.attributes() });

/**
 * A new attribute lands in the cached dictionary at once, so a picker can select it. The create
 * dialog passes `silentError` because it shows the error on the form.
 */
export function useCreateAttribute(meta?: NucleusMutationMeta) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAttributeRequest) => dictionaryApi.createAttribute(body),
    ...(meta ? { meta } : {}),
    onSuccess: (attribute) => {
      queryClient.setQueryData<Attribute[]>(dictionaryKeys.attributes(), (rows) =>
        rows ? [...rows, attribute] : rows,
      );
    },
  });
}

/** 409 ALREADY_EXISTS on `body.value`; `silentError` as for `useCreateAttribute`. */
export function useCreateAttributeValue(meta?: NucleusMutationMeta) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      attributeId,
      body,
    }: {
      attributeId: string;
      body: CreateAttributeValueRequest;
    }) => dictionaryApi.createAttributeValue(attributeId, body),
    ...(meta ? { meta } : {}),
    onSuccess: (value, { attributeId }) => {
      queryClient.setQueryData<Attribute[]>(dictionaryKeys.attributes(), (rows) =>
        rows?.map((row) =>
          row.id === attributeId ? { ...row, values: [...row.values, value] } : row,
        ),
      );
    },
  });
}

/**
 * Label, unit and flags. Product types carry the attribute's label in their assignments, so
 * they are refetched too.
 */
export function useUpdateAttribute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAttributeRequest }) =>
      attributesApi.update(id, body),
    meta: { successMessage: 'attributes:toasts.updated', silentError: true },
    onSuccess: (attribute) => {
      editList(queryClient, (rows) =>
        rows.map((row) => (row.id === attribute.id ? attribute : row)),
      );
      void queryClient.invalidateQueries({ queryKey: dictionaryKeys.productTypes() });
      return refetchAttributes(queryClient);
    },
  });
}

/** 409 RESOURCE_IN_USE while a product type or product still uses it. */
export function useDeleteAttribute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => attributesApi.remove(id),
    meta: { successMessage: 'attributes:toasts.deleted', silentError: true },
    onSuccess: (_, id) => {
      editList(queryClient, (rows) => rows.filter((row) => row.id !== id));
      queryClient.removeQueries({ queryKey: dictionaryKeys.attribute(id) });
      void queryClient.invalidateQueries({ queryKey: dictionaryKeys.productTypes() });
      return refetchAttributes(queryClient);
    },
  });
}

/**
 * Label, swatch and SKU code; the stored value never changes. The answer carries
 * `affectedVariantCount` (generated SKUs still on the old code) for the dialog's note; the cached
 * value row does not keep it.
 */
export function useUpdateAttributeValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      attributeId,
      valueId,
      body,
    }: {
      attributeId: string;
      valueId: string;
      body: UpdateAttributeValueRequest;
    }) => attributesApi.updateValue(attributeId, valueId, body),
    meta: { successMessage: 'attributes:toasts.valueUpdated', silentError: true },
    onSuccess: ({ affectedVariantCount: _affected, ...value }, { attributeId }) => {
      editValues(queryClient, attributeId, (values) => mergeValues(values, [value]));
      return refetchAttributes(queryClient);
    },
  });
}

/** Fills the SKU codes an attribute's values lack (D-161); values with a code are left alone. */
export function useSuggestSkuCodes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attributeId: string) => skuRulesApi.suggestSkuCodes(attributeId),
    onSuccess: ({ items }, attributeId) => {
      const codes = new Map(items.map((item) => [item.id, item.skuCode]));
      editValues(queryClient, attributeId, (values) =>
        values.map((v) => (codes.has(v.id) ? { ...v, skuCode: codes.get(v.id) ?? v.skuCode } : v)),
      );
      return refetchAttributes(queryClient);
    },
  });
}

/**
 * One call with the whole new order (`movedValueIds` works it out); the backend answers the
 * attribute with every position renumbered, which replaces the cached row at once.
 */
export function useReorderAttributeValues() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ attributeId, valueIds }: { attributeId: string; valueIds: string[] }) =>
      attributesApi.reorderValues(attributeId, valueIds),
    onSuccess: (attribute) => {
      editList(queryClient, (rows) =>
        rows.map((row) => (row.id === attribute.id ? attribute : row)),
      );
    },
    onSettled: () => refetchAttributes(queryClient),
  });
}

/** 409 RESOURCE_IN_USE while a product still uses the value. */
export function useDeleteAttributeValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ attributeId, valueId }: { attributeId: string; valueId: string }) =>
      attributesApi.removeValue(attributeId, valueId),
    meta: { successMessage: 'attributes:toasts.valueDeleted', silentError: true },
    onSuccess: (_, { attributeId, valueId }) => {
      editValues(queryClient, attributeId, (values) => values.filter((v) => v.id !== valueId));
      return refetchAttributes(queryClient);
    },
  });
}
